import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './EmailSubscribe.css';

const SUBSCRIPTION_STATUS_KEY = 'movie_subscription_status';
const BANNER_VISIBILITY_KEY = 'banner_visibility';

const EmailSubscribe = () => {
  const [email, setEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  
  const loc = useLocation();
  const navigate = useNavigate();


  // --- Dynamic date calculation ---
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0 = January

  // Create date objects for the 15th of relevant months (midnight local time)
  const currentMonth15 = new Date(currentYear, currentMonth, 15);
  const previousMonth15 = new Date(currentYear, currentMonth - 1, 15);
  const nextMonth15 = new Date(currentYear, currentMonth + 1, 15);

  // Determine last updated and next update based on today's date
  let lastUpdatedDate, nextUpdateDate;

  if (today >= currentMonth15) {
    // Today is on or after the 15th → last update was this month's 15th
    lastUpdatedDate = currentMonth15;
    nextUpdateDate = nextMonth15;
  } else {
    // Today is before the 15th → last update was previous month's 15th
    lastUpdatedDate = previousMonth15;
    nextUpdateDate = currentMonth15;
  }

  // Format dates as "Month DD, YYYY"
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const lastUpdated = formatDate(lastUpdatedDate);
  const nextUpdate = formatDate(nextUpdateDate);
  
  useEffect(() => {
    // Check if user previously hid the banner
    const savedVisibility = localStorage.getItem(BANNER_VISIBILITY_KEY);
    if (savedVisibility === 'hidden') {
      setIsVisible(false);
    }

    // Check URL parameters for subscription messages
    const params = new URLSearchParams(loc.search);
    const message = params.get('message');
    const emailParam = params.get('email_address');
    
    // Handle subscription status from URL parameters
    if (emailParam) {
      setSubscriptionStatus('unconfirmed');
      localStorage.setItem(SUBSCRIPTION_STATUS_KEY, 'unconfirmed');
      setEmail(emailParam);
      // Clean URL without reloading
      navigate(loc.pathname, { replace: true });
    } 
    else if (message === 'confirmed') {
      setSubscriptionStatus('confirmed');
      localStorage.setItem(SUBSCRIPTION_STATUS_KEY, 'confirmed');
      navigate(loc.pathname, { replace: true });
    }
    else {
      // Check localStorage for existing subscription status
      const storedStatus = localStorage.getItem(SUBSCRIPTION_STATUS_KEY);
      if (storedStatus) {
        setSubscriptionStatus(storedStatus);
      }
    }
  }, [loc, navigate]);

  // Clear subscription status after next update
  const toggleVisibility = () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    localStorage.setItem(BANNER_VISIBILITY_KEY, newVisibility ? 'visible' : 'hidden');
  };

  // Short-circuit if banner is hidden
  if (!isVisible) {
    return (
      <div className="show-banner-container">
        <button className="show-banner-button" onClick={toggleVisibility}>
          Subscribe to Updates
        </button>
      </div>
    );
  }

  const renderSubscriptionMessage = () => {
    switch (subscriptionStatus) {
      case 'unconfirmed':
        // I used a sneaky trick to both avoid saving the email of the
        // user in the local storage and inconsistencies when
        // reloading the page (because 'email' will be the empty string)
        return (
          <div className="confirmation-message banner">
            <div className="icon">📬</div>
            <p>
             {
             email ? <>Almost there! Please check your inbox at <strong> {email}</strong> to confirm your subscription.</>
                 : <>Almost there! Please check your inbox to confirm your subscription.</>
             }
            </p>
            <div className="banner-actions">
              <button 
                className="change-email"
                onClick={() => {
                  localStorage.removeItem(SUBSCRIPTION_STATUS_KEY);
                  setSubscriptionStatus(null);
                }}
              >
                Use different email
              </button>
              <button className="hide-banner" onClick={toggleVisibility}>
                Hide This
              </button>
            </div>
          </div>
        );
        
      case 'confirmed':
        return (
          <div className="confirmation-message banner">
            <div className="success-icon">✓</div>
            <p>
              You're subscribed! We'll notify you about the next update on {nextUpdate}.
            </p>
            <div className="banner-actions">
              <button className="hide-banner" onClick={toggleVisibility}>
                Hide This
              </button>
            </div>
          </div>
        );
        
      default:
        return (
          <form action="https://buttondown.com/api/emails/embed-subscribe/cinerecs" method="POST" className="subscribe-form banner">
            <div className="input-group">
              <input
                type="email"
                value={email}
                name="email"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
              />
              <button 
                type="submit" 
                className="banner"
              >
                Notify Me
              </button>
            </div>
            <div className="banner-actions">
              <button className="hide-banner" onClick={toggleVisibility}>
                Not Interested
              </button>
            </div>
          </form>
        );
    }
  };

  return (
    <div className="subscribe-banner dark-theme">
      <button className="close-button" onClick={toggleVisibility} aria-label="Close">
        ×
      </button>
      
      <div className="banner-content">
        <div className="banner-text">
          <h3 style={{ fontFamily: "'Century Gothic', sans-serif", fontWeight: "bold" }}>Fresh Movie Picks, Every Month</h3>
          <div className="disclaimer">
            <p>No noise • Just one update • Always worth it</p>
          </div>
        
          <div className="banner-content">
              <span className="info-label">Last update: <div className="update-date">{lastUpdated}</div></span>
              <span className="info-label">Next update: <div className="update-date">{nextUpdate}</div></span>
          </div>
        
        {renderSubscriptionMessage()}
        </div>
      </div>
    </div>
  );
};

export default EmailSubscribe;
